import { AxiosError } from 'axios';
import { post, get, patch, put } from './https';
import {
  AdminPermissions,
  AdminRole,
  AdminDepartment,
  FeatureOverrides,
} from '../utils/adminPermissions';

interface LoginResponse {
  status: boolean;
  message: string;
  assessToken: string;
  refreshToken?: string;
  type?: number;
  role?: AdminRole | string;
  department?: AdminDepartment | null;
  name?: string;
  permissions?: AdminPermissions;
  useCustomPermissions?: boolean;
}

export interface AdminStaffMember {
  _id: string;
  name: string;
  email: string;
  role: AdminRole;
  department: AdminDepartment | null;
  departments?: AdminDepartment[];
  location?: AdminLocation | null;
  isActive?: boolean;
  useCustomPermissions?: boolean;
  customPermissions?: { modules: AdminPermissions['modules']; features?: FeatureOverrides };
  resolvedPermissions?: AdminPermissions;
  defaultPermissions?: AdminPermissions;
  createdAt?: string;
}

export interface AdminAuditLogEntry {
  _id: string;
  actorId?: string;
  actorName?: string;
  targetId: string;
  targetName?: string;
  action:
    | 'STAFF_CREATED'
    | 'ROLE_CHANGED'
    | 'DEPARTMENT_CHANGED'
    | 'PERMISSIONS_UPDATED'
    | 'PERMISSIONS_RESET'
    | 'PERMISSIONS_CLONED'
    | 'STAFF_ACTIVATED'
    | 'STAFF_DEACTIVATED'
    | 'STAFF_DELETED';
  before?: unknown;
  after?: unknown;
  createdAt: string;
}

export interface AdminLocation {
  zoneId?: string | null;
  name?: string;
}

export interface AdminZoneOption {
  _id: string;
  name: string;
  isActive?: boolean;
}

const PostLogin = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<LoginResponse> => {
  try {
    const response = await post('/api/v1/admin/login', { email, password });
    if (response.status == 200) {
      return response.data;
    }
    throw Error('Login Failed');
  } catch (error) {
    const errorMessage =
      error instanceof AxiosError
        ? error.response?.data?.message || 'An error occurred during login.'
        : 'An unknown error occurred. Please try again later.';
    throw new Error(errorMessage);
  }
};

const getAdminStaffList = async () => {
  const response = await get('/api/v1/admin/staff/list');
  return response.data;
};

const createAdminStaff = async (payload: {
  name: string;
  email: string;
  password: string;
  role: AdminRole;
  department: AdminDepartment;
  departments?: AdminDepartment[];
  locationZoneId?: string;
  locationName?: string;
}) => {
  const response = await post('/api/v1/admin/staff/create', payload);
  return response.data;
};

const updateAdminStaff = async (
  id: string,
  payload: {
    name?: string;
    role?: AdminRole;
    department?: AdminDepartment | null;
    departments?: AdminDepartment[];
    locationZoneId?: string;
    locationName?: string;
    location?: AdminLocation | null;
  },
) => {
  return patch(`/api/v1/admin/staff/${id}`, payload);
};

const getAdminZoneOptions = async () => {
  const response = await get('/api/v1/admin/staff/zones');
  return response.data as {
    status: boolean;
    data: AdminZoneOption[];
  };
};

const createAdminZoneOption = async (name: string) => {
  const response = await post('/api/v1/admin/staff/zones', { name });
  return response.data as {
    status: boolean;
    message: string;
    data: AdminZoneOption;
  };
};

const updateAdminStaffPermissions = async (
  id: string,
  payload: {
    useCustomPermissions: boolean;
    modules?: AdminPermissions['modules'];
    features?: FeatureOverrides;
  },
) => {
  return put(`/api/v1/admin/staff/${id}/permissions`, payload);
};

const resetAdminStaffPermissions = async (id: string) => {
  const response = await post(`/api/v1/admin/staff/${id}/permissions/reset`, {});
  return response.data;
};

const toggleAdminStaffActive = async (id: string) => {
  return patch(`/api/v1/admin/staff/${id}/toggle-active`, {});
};

const bulkDeleteAdminStaff = async (
  adminIds: string[],
): Promise<{ status: boolean; message: string; deletedCount: number }> => {
  const response = await post('/api/v1/admin/staff/bulk-delete', {
    adminIds,
  });
  return response.data;
};

const getAdminStaffAuditLog = async (
  id: string,
): Promise<{ status: boolean; data: AdminAuditLogEntry[] }> => {
  const response = await get(`/api/v1/admin/staff/${id}/audit-log`);
  return response.data;
};

const cloneAdminStaffPermissions = async (id: string, sourceAdminId: string) => {
  const response = await post(`/api/v1/admin/staff/${id}/clone-permissions`, {
    sourceAdminId,
  });
  return response.data;
};

export {
  PostLogin,
  getAdminStaffList,
  createAdminStaff,
  updateAdminStaff,
  updateAdminStaffPermissions,
  resetAdminStaffPermissions,
  toggleAdminStaffActive,
  getAdminZoneOptions,
  createAdminZoneOption,
  bulkDeleteAdminStaff,
  getAdminStaffAuditLog,
  cloneAdminStaffPermissions,
};
